import Papa from "papaparse";
import { emptyKycDetails, isDateField, kycFields, type KycRow } from "./kyc";

const detailHeaders = ["ICCID", ...kycFields.map(([, label]) => label)];
const choiceHeader = "Use customer KYC for activation";
const emailHeader = "Customer email";
export const kycHeaders = [...detailHeaders, choiceHeader, emailHeader];
const keys = ["iccid", ...kycFields.map(([field]) => field)] as const;
export type ParsedKycRow = {
  rowNumber: number;
  details: KycRow;
  errors: string[];
};

export function parseKycCells(cells: unknown[][]): ParsedKycRow[] {
  const headers = (cells[0] || []).map((value) => String(value ?? "").trim());
  if (
    detailHeaders.some(
      (header) => headers.filter((value) => value === header).length !== 1,
    ) ||
    [choiceHeader, emailHeader].some(
      (header) => headers.filter((value) => value === header).length > 1,
    )
  ) {
    throw new Error(
      "Use the downloaded template with each required column appearing once.",
    );
  }
  const data = cells
    .slice(1)
    .map((values, index) => ({ values, rowNumber: index + 2 }))
    .filter(({ values }) =>
      values.some(
        (value) =>
          value !== null && value !== undefined && String(value).trim() !== "",
      ),
    );
  if (data.length < 1 || data.length > 200) {
    throw new Error("The file must contain between 1 and 200 ICCID rows.");
  }
  const parsed = data.map(({ values, rowNumber }) => {
    const details: KycRow = {
      iccid: "",
      ...emptyKycDetails(),
      useCustomerKyc: false,
    };
    const errors: string[] = [];
    keys.forEach((key, index) => {
      const value = values[headers.indexOf(kycHeaders[index])];
      if (value === undefined || value === null) {
        return;
      }
      if (
        (key === "iccid" || key === "passportNumber") &&
        typeof value !== "string"
      ) {
        errors.push(
          `${kycHeaders[index]} must be stored as text. ` +
            "Re-enter it from the original source.",
        );
      }
      if (value instanceof Date && isDateField(key)) {
        details[key] = Number.isNaN(value.getTime())
          ? ""
          : value.toISOString().slice(0, 10);
      } else if (typeof value === "string" || typeof value === "number") {
        details[key] = String(value).trim();
      } else {
        errors.push(
          `${kycHeaders[index]} must contain a plain value, ` +
            "not a formula or object.",
        );
      }
      if (
        (key === "iccid" || key === "passportNumber") &&
        /^[+-]?\d+(\.\d+)?[eE][+-]?\d+$/.test(details[key])
      ) {
        errors.push(
          `${kycHeaders[index]} contains scientific notation. ` +
            "Re-enter it as text from the original source.",
        );
      }
    });
    const choice = values[headers.indexOf(choiceHeader)] ?? "";
    if (typeof choice !== "string" || !/^(yes|no)?$/i.test(choice.trim())) {
      errors.push(`${choiceHeader} must be Yes, No, or blank (No).`);
    } else {
      details.useCustomerKyc = choice.trim().toLowerCase() === "yes";
    }
    if (details.useCustomerKyc) {
      const email = values[headers.indexOf(emailHeader)] ?? "";
      if (typeof email !== "string") {
        errors.push(`${emailHeader} must contain plain text.`);
      } else {
        details.customerEmail = email.trim();
      }
    }
    return { rowNumber, details, errors };
  });
  const counts = new Map<string, number>();
  parsed.forEach(({ details }) =>
    counts.set(details.iccid, (counts.get(details.iccid) || 0) + 1),
  );
  parsed.forEach((row) => {
    if ((counts.get(row.details.iccid) || 0) > 1) {
      row.errors.push("Each ICCID must appear only once.");
    }
  });
  return parsed;
}

export function parseKycCsv(text: string) {
  const parsed = Papa.parse<string[]>(text.replace(/^\uFEFF/, ""), {
    dynamicTyping: false,
  });
  if (parsed.errors.some((error) => error.code !== "UndetectableDelimiter")) {
    throw new Error(
      "The CSV could not be read. Check its quotes and column separators.",
    );
  }
  return parseKycCells(parsed.data);
}

export async function parseKycExcel(buffer: ArrayBuffer) {
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const sheet = workbook.worksheets[0];
  if (!sheet) {
    throw new Error("The workbook has no worksheet.");
  }
  if (sheet.actualRowCount > 201) {
    throw new Error("The file must contain at most 200 ICCID rows.");
  }
  const cells: unknown[][] = [];
  sheet.eachRow({ includeEmpty: true }, (row) => {
    const values = row.values;
    cells.push(Array.isArray(values) ? values.slice(1) : []);
  });
  return parseKycCells(cells);
}

export function csvTemplate() {
  return Papa.unparse({ fields: kycHeaders, data: [] }).trimEnd() + "\r\n";
}

export async function excelTemplate() {
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("KYC");
  sheet.columns = kycHeaders.map((header) => ({
    header,
    width: 24,
    style: { numFmt: "@" },
  }));
  sheet.getRow(1).font = { bold: true };
  sheet.views = [{ state: "frozen", ySplit: 1 }];
  return workbook.xlsx.writeBuffer();
}

export async function downloadKycTemplate(format: "csv" | "xlsx") {
  const content =
    format === "csv" ? csvTemplate() : new Uint8Array(await excelTemplate());
  const blob = new Blob([content], {
    type:
      format === "csv"
        ? "text/csv;charset=utf-8"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `kyc-template.${format}`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
